import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { Api } from '../../../api/gse/api';
import {
  getAllMaintenanceSchedules,
  getBackJobsSummary,
  getOverdueJobsSummary,
  getAllOverDueJobCards,
  getInspectorDashboardSummary,
  getMaintenanceAlertsOfEquipments,
  getMaintenanceWeeklyOverview,
  getPlannerDashboardSummary,
  getSupervisorDashboardSummary,
  getTechnicianEfficiency,
  getAllPmNotifications,
  getWaitingForSpares,
} from '../../../api/gse/functions';
import { GetAllOverDueJobCards$Params } from '../../../api/gse/fn/maintenance-dashboard/get-all-over-due-job-cards';
import { getMaintenanceOverview } from '../../../api/gse/fn/maintenance-dashboard/get-maintenance-overview';
import { MaintenanceScheduleMinimalDto } from '../../../api/gse/models/maintenance-schedule-minimal-dto';
import { AlertTypes } from '../../../api/gse/models/alert-types';
import { MaintenanceAlertDtoResponse } from '../../../api/gse/models/maintenance-alert-dto-response';
import { MaintenanceDashboardSummaryDto } from '../../../api/gse/models/maintenance-dashboard-summary-dto';
import { InspectionReadyJobsSummaryDto } from '../../../api/gse/models/inspection-ready-jobs-summary-dto';
import { InspectorDashboardSummaryDto } from '../../../api/gse/models/inspector-dashboard-summary-dto';
import { JobCardSummaryDto } from '../../../api/gse/models/job-card-summary-dto';
import { MaintenanceSummaryDto } from '../../../api/gse/models/maintenance-summary-dto';
import { OverdueJobsSummaryDto } from '../../../api/gse/models/overdue-jobs-summary-dto';
import { MaintenanceWeeklyOverviewDto } from '../../../api/gse/models/maintenance-weekly-overview-dto';
import { BackJobsSummaryDto } from '../../../api/gse/models/back-jobs-summary-dto';
import { SupervisorDashboardSummaryDto } from '../../../api/gse/models/supervisor-dashboard-summary-dto';
import { MaintenanceOverviewCountResponseDto } from '../../../api/gse/models/maintenance-overview-count-response-dto';
import { TechnicianEfficiencyDto } from '../../../api/gse/models/technician-efficiency-dto';
import { JobCardDto, JobCardMinimalDtoWithEquipment } from 'src/app/api/gse/models';

@Injectable({ providedIn: 'root' })
export class MaintenanceDashboardService {
  private readonly api = inject(Api);

  /**
   * GET /api/gse/dashboard/maintenance/overdue-jobs
   * Requires VIEW_OVER_DUE_JOB_CARDS privilege.
   */
  getAllOverDueJobCards(params: GetAllOverDueJobCards$Params): Observable<{
    data: JobCardDto[];
    page: number;
    total?: number;
    totalPages: number;
  }> {
    return this.api.invoke(getAllOverDueJobCards, params).pipe(
      map((res) => ({
        data: res.data ?? [],
        page: res.page?.page ?? params.page ?? 0,
        total: res.page?.totalElements,
        totalPages: res.page?.totalPages ?? 1,
      }))
    );
  }

  /**
   * GET /api/gse/dashboard/maintenance/planner-summary
   * Requires PLANNER_DASHBOARD_SUMMARY_VIEW. Merges multiple summary rows when the API returns an array.
   * Errors propagate to callers (e.g. NgRx effects).
   */
  getPlannerSummaryMerged(): Observable<MaintenanceDashboardSummaryDto | null> {
    return this.api.invoke(getPlannerDashboardSummary, {}).pipe(
      map((res) => {
        const items = res.data ?? [];
        if (items.length === 0) {
          return null;
        }
        return this.mergeSummaries(items);
      })
    );
  }

  /**
   * GET /api/gse/dashboard/maintanance/supervisor-summary
   * Requires SUPERVISOR_DASHBOARD_SUMMARY_VIEW. Merges multiple summary rows when the API returns an array.
   * Errors propagate to callers (e.g. NgRx effects).
   */
  getSupervisorSummary(): Observable<SupervisorDashboardSummaryDto | null> {
    return this.api.invoke(getSupervisorDashboardSummary, {}).pipe(
      map((res) => {
        const items = res.data ?? [];
        if (items.length === 0) return null;
        if (items.length === 1) return items[0];
        return this.mergeSupervisorSummaries(items);
      })
    );
  }

  /**
   * GET /api/gse/dashboard/maintenance/overview
   * Requires VIEW_DASHBOARD_MAINTENANCE_OVERVIEW privilege (per OpenAPI).
   * Merges multiple rows when API returns an array.
   */
  getMaintenanceOverviewMerged(): Observable<MaintenanceOverviewCountResponseDto | null> {
    return this.api.invoke(getMaintenanceOverview, {}).pipe(
      map((res) => {
        const items = res.data ?? [];
        if (items.length === 0) return null;
        return items.reduce<MaintenanceOverviewCountResponseDto>(
          (acc, cur) => ({
            corrective: (acc.corrective ?? 0) + (cur.corrective ?? 0),
            preventiveCount: (acc.preventiveCount ?? 0) + (cur.preventiveCount ?? 0),
          }),
          { corrective: 0, preventiveCount: 0 }
        );
      })
    );
  }

  getPmNotifications(params: {
    page?: number;
    size?: number;
    sort?: string;
    search?: string;
  }): Observable<{ data: MaintenanceScheduleMinimalDto[]; total?: number }> {
    return this.api.invoke(getAllPmNotifications, {
      page: params.page,
      size: params.size,
      sort: params.sort,
      search: params.search,
    }).pipe(map((res) => ({
      data: res.data ?? [],
      total: res.page?.totalElements,
    })));
  }

  /**
   * GET /api/gse/maintenance-schedules/daily-checkins
   * Requires MAINTENANCE_SCHEUDLES_DAILY_CHECK_INS or MAINTENANCE_SCHEDULES_DAILY_CHECK_INS_CREATED.
   */
  getDailyCheckins(params: {
    page?: number;
    size?: number;
    sort?: string;
    from?: string;
    to?: string;
  }): Observable<MaintenanceScheduleMinimalDto[]> {
    return this.api.invoke(getAllMaintenanceSchedules, {
      page: params.page,
      size: params.size,
      sort: params.sort,
      from: params.from,
      to: params.to,
    }).pipe(map((res) => res.data ?? []));
  }

  /**
   * GET /api/gse/dashboard/maintenance/back-jobs-summary
   * Requires MAINTENANCE_DASHBOARD_BACKJOB_VIEW privilege (per OpenAPI).
   * Merges multiple summary rows when the API returns an array.
   */
  getBackJobsSummaryMerged(): Observable<BackJobsSummaryDto | null> {
    return this.api.invoke(getBackJobsSummary, {}).pipe(
      map((res) => {
        const items = res.data ?? [];
        if (items.length === 0) return null;
        return items.reduce<BackJobsSummaryDto>(
          (acc, cur) => ({
            scheduled: (acc.scheduled ?? 0) + (cur.scheduled ?? 0),
            pending: (acc.pending ?? 0) + (cur.pending ?? 0),
          }),
          { scheduled: 0, pending: 0 }
        );
      })
    );
  }

  private mergeSupervisorSummaries(items: SupervisorDashboardSummaryDto[]): SupervisorDashboardSummaryDto {
    return items.reduce<SupervisorDashboardSummaryDto>((acc, cur) => ({
      preventiveMaintenance: this.mergeMaintenanceSummary(acc.preventiveMaintenance, cur.preventiveMaintenance),
      correctiveMaintenance: this.mergeMaintenanceSummary(acc.correctiveMaintenance, cur.correctiveMaintenance),
      jobCards: {
        total: (acc.jobCards?.total ?? 0) + (cur.jobCards?.total ?? 0),
        open: (acc.jobCards?.open ?? 0) + (cur.jobCards?.open ?? 0),
        backJobs: (acc.jobCards?.backJobs ?? 0) + (cur.jobCards?.backJobs ?? 0),
        completed: (acc.jobCards?.completed ?? 0) + (cur.jobCards?.completed ?? 0),
      },
      equipments: {
        total: (acc.equipments?.total ?? 0) + (cur.equipments?.total ?? 0),
        active: (acc.equipments?.active ?? 0) + (cur.equipments?.active ?? 0),
        rented: (acc.equipments?.rented ?? 0) + (cur.equipments?.rented ?? 0),
        scrapped: (acc.equipments?.scrapped ?? 0) + (cur.equipments?.scrapped ?? 0),
      },
    }), {});
  }

  private mergeMaintenanceSummary(a?: MaintenanceSummaryDto, b?: MaintenanceSummaryDto): MaintenanceSummaryDto {
    return {
      total: (a?.total ?? 0) + (b?.total ?? 0),
      open: (a?.open ?? 0) + (b?.open ?? 0),
      inProgress: (a?.inProgress ?? 0) + (b?.inProgress ?? 0),
      inspection: (a?.inspection ?? 0) + (b?.inspection ?? 0),
      completed: (a?.completed ?? 0) + (b?.completed ?? 0),
    };
  }

  /**
   * GET /api/gse/dashboard/maintenance/alerts
   * Requires MAINTENANCE_DASHBOARD_ALERT_VIEW privilege (per OpenAPI).
   */
  getMaintenanceAlerts(params: {
    from: string;
    to: string;
    type?: AlertTypes;
    page?: number;
    size?: number;
    sort?: string;
  }): Observable<{ data: MaintenanceAlertDtoResponse[]; total?: number }> {
    return this.api.invoke(getMaintenanceAlertsOfEquipments, {
      from: params.from,
      to: params.to,
      type: params.type,
      page: params.page,
      size: params.size,
      sort: params.sort,
    }).pipe(
      map((res) => ({
        data: res.data ?? [],
        total: res.page?.totalElements,
      }))
    );
  }

  /**
   * GET /api/gse/dashboard/maintenance/job-card-overview
   * Requires MAINTENANCE_DASHBOARD_OVERVIEW_VIEW privilege (per OpenAPI).
   */
  getMaintenanceWeeklyOverview(params: {
    from: string;
    to: string;
  }): Observable<MaintenanceWeeklyOverviewDto[]> {
    return this.api.invoke(getMaintenanceWeeklyOverview, {
      from: params.from,
      to: params.to,
    }).pipe(map((res) => res.data ?? []));
  }

  /**
   * GET /api/gse/dashboard/maintenance/inspector-summary
   * Requires INSPECTOR_DASHBOARD_SUMMARY_VIEW privilege (per OpenAPI).
   * Merges multiple summary rows when the API returns an array.
   */
  getInspectorSummaryMerged(): Observable<InspectorDashboardSummaryDto | null> {
    return this.api.invoke(getInspectorDashboardSummary, {}).pipe(
      map((res) => {
        const items = res.data ?? [];
        if (items.length === 0) return null;
        if (items.length === 1) return items[0];
        return items.reduce<InspectorDashboardSummaryDto>(
          (acc, cur) => ({
            preventiveMaintenance: this.mergeMaintenanceSummary(
              acc.preventiveMaintenance,
              cur.preventiveMaintenance
            ),
            correctiveMaintenance: this.mergeMaintenanceSummary(
              acc.correctiveMaintenance,
              cur.correctiveMaintenance
            ),
            jobCards: this.mergeJobCardsSummary(acc.jobCards, cur.jobCards),
            inspectionReadyJobs: this.mergeInspectionReadyJobsSummary(
              acc.inspectionReadyJobs,
              cur.inspectionReadyJobs
            ),
          }),
          {}
        );
      })
    );
  }

  /**
   * GET /api/gse/dashboard/maintenance/overdue-jobs-summary
   * Requires MAINTENANCE_DASHBOARD_OVERDUE_JOB_CARD_SUMMARY privilege (per OpenAPI).
   * Merges multiple summary rows when the API returns an array.
   */
  getOverdueJobsSummaryMerged(): Observable<OverdueJobsSummaryDto | null> {
    return this.api.invoke(getOverdueJobsSummary, {}).pipe(
      map((res) => {
        const items = res.data ?? [];
        if (items.length === 0) return null;
        return items.reduce<OverdueJobsSummaryDto>(
          (acc, cur) => ({
            pending: (acc.pending ?? 0) + (cur.pending ?? 0),
            escalated: (acc.escalated ?? 0) + (cur.escalated ?? 0),
          }),
          { pending: 0, escalated: 0 }
        );
      })
    );
  }

  /**
   * GET /api/gse/dashboard/maintenance/technician-leaderboard
   * Requires VIEW_DASHBOARD_TECHNICIAN_LEADERBOARD privilege.
   */
  getTechnicianEfficiency(params: {
    limit?: number;
    from: string;
    to: string;
  }): Observable<TechnicianEfficiencyDto[]> {
    return this.api
      .invoke(getTechnicianEfficiency, {
        limit: params.limit,
        from: params.from,
        to: params.to,
      })
      .pipe(map((res) => res.data ?? []));
  }

  getWaitingForSpares(params: {
    page?: number;
    size?: number;
    sort?: string;
  }): Observable<{ data: JobCardMinimalDtoWithEquipment[]; total?: number }> {
    return this.api
      .invoke(getWaitingForSpares, {
        page: params.page,
        size: params.size,
        sort: params.sort,
      })
      .pipe(
        map((res) => ({
          data: res.data ?? [],
          total: res.page?.totalElements,
        }))
      );
  }

  private mergeJobCardsSummary(a?: JobCardSummaryDto, b?: JobCardSummaryDto): JobCardSummaryDto {
    return {
      total: (a?.total ?? 0) + (b?.total ?? 0),
      open: (a?.open ?? 0) + (b?.open ?? 0),
      backJobs: (a?.backJobs ?? 0) + (b?.backJobs ?? 0),
      completed: (a?.completed ?? 0) + (b?.completed ?? 0),
    };
  }

  private mergeInspectionReadyJobsSummary(
    a?: InspectionReadyJobsSummaryDto,
    b?: InspectionReadyJobsSummaryDto
  ): InspectionReadyJobsSummaryDto {
    return {
      total: (a?.total ?? 0) + (b?.total ?? 0),
      pending: (a?.pending ?? 0) + (b?.pending ?? 0),
      completed: (a?.completed ?? 0) + (b?.completed ?? 0),
      failed: (a?.failed ?? 0) + (b?.failed ?? 0),
    };
  }

  private mergeSummaries(items: MaintenanceDashboardSummaryDto[]): MaintenanceDashboardSummaryDto {
    return items.reduce<MaintenanceDashboardSummaryDto>((acc, cur) => ({
      serviceRequests: {
        totalCount: (acc.serviceRequests?.totalCount ?? 0) + (cur.serviceRequests?.totalCount ?? 0),
        openCheckIns: (acc.serviceRequests?.openCheckIns ?? 0) + (cur.serviceRequests?.openCheckIns ?? 0),
        completedCheckIns:
          (acc.serviceRequests?.completedCheckIns ?? 0) + (cur.serviceRequests?.completedCheckIns ?? 0),
      },
      equipments: {
        activeCount: (acc.equipments?.activeCount ?? 0) + (cur.equipments?.activeCount ?? 0),
        rentedCount: (acc.equipments?.rentedCount ?? 0) + (cur.equipments?.rentedCount ?? 0),
        scrappedCount: (acc.equipments?.scrappedCount ?? 0) + (cur.equipments?.scrappedCount ?? 0),
      },
      jobCards: {
        correctiveCount: (acc.jobCards?.correctiveCount ?? 0) + (cur.jobCards?.correctiveCount ?? 0),
        preventiveCount: (acc.jobCards?.preventiveCount ?? 0) + (cur.jobCards?.preventiveCount ?? 0),
      },
      complianceEquipment: {
        tuvExpiresCount:
          (acc.complianceEquipment?.tuvExpiresCount ?? 0) +
          (cur.complianceEquipment?.tuvExpiresCount ?? 0),
        ramPassExpiresCount:
          (acc.complianceEquipment?.ramPassExpiresCount ?? 0) +
          (cur.complianceEquipment?.ramPassExpiresCount ?? 0),
      },
    }), {});
  }
}
